"use client";

import Link from "next/link";
import {
  BookOpen,
  Target,
  Users,
  TrendingUp,
  Sparkles,
  Briefcase,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Compass,
} from "lucide-react";
import { motion } from "framer-motion";
import { STATS, SUCCESS_RATE } from "@/lib/stats";
import { WhatsAppIcon } from "@/components/BrandIcons";

export default function JoinPage() {
  return (
    <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-c2e-blue to-c2e-blue-light text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold sm:text-5xl mb-4">
              Rejoignez le C2E
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Ensemble, construisons votre réussite. Inscrivez-vous dès
              aujourd'hui !
            </p>
          </div>
        </div>
      </section>

      {/* Pourquoi nous rejoindre */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Pourquoi rejoindre le C2E ?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: "Cours de soutien de qualité",
                description:
                  "Bénéficiez de l'accompagnement de tuteurs qualifiés dans toutes les matières principales.",
              },
              {
                icon: Target,
                title: "Ateliers de leadership",
                description:
                  "Développez vos compétences en communication, gestion d'équipe et prise de décision.",
              },
              {
                icon: Users,
                title: "Communauté dynamique",
                description: `Rejoignez une famille de ${STATS.students} élèves et étudiants motivés.`,
              },
              {
                icon: TrendingUp,
                title: `${SUCCESS_RATE.value} de réussite en ${new Date().getFullYear()}`,
                description:
                  "Nos membres obtiennent d'excellents résultats aux examens nationaux.",
              },
              {
                icon: Sparkles,
                title: "Événements enrichissants",
                description:
                  "Participez à des compétitions, sorties éducatives et événements culturels.",
              },
              {
                icon: Briefcase,
                title: "Préparation pour l'avenir",
                description:
                  "Acquérez des compétences transversales essentielles pour votre carrière.",
              },
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                className="bg-c2e-gray-light rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <benefit.icon className="h-12 w-12 text-c2e-blue mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos offres */}
      <section className="py-16 bg-c2e-gray-light">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Nos offres de cours
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Des programmes adaptés à tous les niveaux, de la 3ème à la Terminale
          </p>

          <div className="flex flex-col lg:flex-row gap-8 max-w-5xl mx-auto">
            {/* Cours de soutien */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex-1 flex flex-col h-full">
              <div className="bg-gradient-to-br from-c2e-blue to-c2e-blue-light p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">
                  Cours de Soutien Scolaire
                </h3>
                <p className="text-blue-100">Programme régulier</p>
              </div>
              <div className="p-8 flex flex-col justify-between h-full">
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Classes disponibles
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "3ème",
                      "2nd CD",
                      "1A4",
                      "1G2",
                      "1G3",
                      "1C4",
                      "1D",
                      "Tle A4",
                      "Tle G",
                      "Tle D",
                      "Tle C4",
                    ].map((classe) => (
                      <span
                        key={classe}
                        className="bg-c2e-blue-lightest text-c2e-blue px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {classe}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-3">Matières</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {[
                      "Mathématiques",
                      "Physique-Chimie",
                      "SVT",
                      "Anglais",
                      "Espagnol",
                      "Allemand",
                      "Français",
                      "Philosophie",
                      "Économie",
                      "Comptabilité",
                      "Techniques commerciales",
                      "Mathématiques générales",
                      "Mathématiques financières",
                    ].map((matiere) => (
                      <li key={matiere} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-c2e-blue flex-shrink-0" />
                        {matiere}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600">
                    <strong>Horaires :</strong> Samedi - Dimanche: 07h00 - 18h00
                    & 14h00 - 18h00
                  </p>
                </div>
              </div>
            </div>

            {/* Autres programmes */}
            <div className="space-y-6 flex-1">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="h-8 w-8 text-c2e-blue" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Ateliers de Leadership
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Sessions hebdomadaires pour développer vos compétences en
                  communication, gestion d'équipe et prise de décision.
                </p>
                <p className="text-sm text-c2e-blue font-semibold">
                  Saisonnier
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="h-8 w-8 text-c2e-blue" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Bibliothèque & Ressources
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Accès illimité à notre bibliothèque, manuels scolaires et
                  ressources numériques.
                </p>
                <p className="text-sm text-c2e-blue font-semibold">
                  Lundi - Samedi : 08h00 - 17h00
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-8 w-8 text-c2e-blue" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Événements & Activités
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Compétitions académiques, événements culturels, sorties
                  éducatives et plus encore.
                </p>
                <p className="text-sm text-c2e-blue font-semibold">
                  Saisonnier
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Compass className="h-8 w-8 text-c2e-blue" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Orientation Post-BEPC & Post-BAC
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Conseils et accompagnement personnalisé pour choisir votre
                  voie après le BEPC ou votre poursuite d'études et insertion
                  professionnelle après le Bac.
                </p>
                <p className="text-sm text-c2e-blue font-semibold">
                  Sur demande
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment s'inscrire */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Comment s'inscrire ?
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-c2e-blue text-white rounded-full flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Contactez-nous
                </h3>
                <p className="text-gray-600">
                  Appelez-nous au{" "}
                  <a
                    href="tel:+22870013605"
                    className="text-c2e-blue font-semibold"
                  >
                    +228 70 01 36 05
                  </a>{" "}
                  ou écrivez-nous sur WhatsApp pour obtenir plus d'informations.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-c2e-blue text-white rounded-full flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Visitez nos locaux
                </h3>
                <p className="text-gray-600">
                  Passez à notre siège à Agoe Kititidjan pour rencontrer notre
                  équipe et découvrir nos installations.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-c2e-blue text-white rounded-full flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Complétez votre inscription
                </h3>
                <p className="text-gray-600">
                  Remplissez le formulaire d'inscription et fournissez les
                  documents nécessaires (photocopie CNI/extrait de naissance,
                  photo d'identité, bulletin scolaire).
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-c2e-red text-white rounded-full flex items-center justify-center font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Commencez votre parcours
                </h3>
                <p className="text-gray-600">
                  Bienvenue au C2E ! Recevez votre carte de membre et commencez
                  à profiter de tous nos services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-c2e-blue to-c2e-blue-light text-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Prêt à commencer votre aventure avec le C2E ?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Inscrivez-vous dès aujourd'hui et bénéficiez d'un accompagnement de
            qualité
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="https://wa.me/22870013605?text=Bonjour%2C%20je%20souhaite%20m'inscrire%20au%20C2E"
              target="_blank"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-green-600 px-8 py-4 text-lg font-semibold hover:bg-green-700 transition-colors"
            >
              <WhatsAppIcon className="h-6 w-6" />
              S'inscrire via WhatsApp
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white px-8 py-4 text-lg font-semibold hover:bg-white hover:text-c2e-blue transition-all"
            >
              Nous contacter
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
